// 목록 화면 상단의 공용 검색 / 필터 툴바.
// useDataGrid의 filters / setFilters와 기존 functions/generateFilter의 updateFilters를
// 그대로 사용한다(데이터 레이어 불변). route는 fields 배열로 검색 폼을 선언하고,
// sx / className / slots로 개별 스타일·동작을 얹는다.

import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import type { SxProps, Theme } from "@mui/material/styles";
import type { CrudFilters, CrudOperators } from "@refinedev/core";
import { updateFilters } from "functions/generateFilter";

export type SearchFieldType = "text" | "select" | "date";

export interface SearchFieldConfig {
  // 필터 field 키 (예: "agentName")
  name: string;
  label: string;
  type: SearchFieldType;
  // 미지정 시 text→"contains", select/date→"eq"
  operator?: CrudOperators;
  // select 전용 옵션
  options?: { value: string; label: string }[];
  placeholder?: string;
  // 이 필드의 최소 폭(px 또는 CSS 길이). grid 셀 minWidth로 반영
  width?: number | string;
  // date에서 부분 입력값 가드 등. false를 반환하면 setFilters 스킵
  validate?: (value: string) => boolean;
  helperText?: string;
}

export interface SearchToolbarProps {
  fields: SearchFieldConfig[];
  filters: CrudFilters;
  setFilters: (filters: CrudFilters) => void;
  // 기본 동작: fields에 선언된 필터만 제거(외부 필터 보존)
  onReset?: () => void;
  title?: string;
  // route override slot — 루트 Paper에 병합
  sx?: SxProps<Theme>;
  // route override slot — .module.css
  className?: string;
  slots?: {
    leading?: React.ReactNode;
    actions?: React.ReactNode;
  };
}

const getFilterValue = (filters: CrudFilters, field: string): string => {
  const found = filters.find((f) => "field" in f && f.field === field);
  const value = found && "value" in found ? found.value : undefined;
  return value === null || value === undefined ? "" : String(value);
};

const defaultOperator = (type: SearchFieldType): CrudOperators =>
  type === "text" ? "contains" : "eq";

export const SearchToolbar: React.FC<SearchToolbarProps> = ({
  fields,
  filters,
  setFilters,
  onReset,
  title = "검색",
  sx,
  className,
  slots,
}) => {
  // 비제어 text 입력값 임시 저장(검색 버튼으로 일괄 반영)
  const textValuesRef = React.useRef<Record<string, string>>({});
  // 초기화 시 비제어 필드를 강제 remount 하기 위한 토큰
  const [resetToken, setResetToken] = React.useState(0);

  const applyFilter = (
    field: string,
    operator: CrudOperators,
    value: string
  ) => {
    setFilters(updateFilters(filters, field, operator, value));
  };

  // text/date 필드 버퍼(textValuesRef)를 한 번에 필터로 반영한다.
  // Enter 키와 "검색" 버튼이 완전히 동일한 경로를 타도록 통일한다.
  // date도 select처럼 값을 정하는 즉시 검색되면 검색 버튼/Enter 흐름과 어긋나고,
  // (조회만 하고 확정하지 않은) 값이 그대로 필터/URL에 반영돼 새로고침 후에도 남는
  // 문제가 있어 text와 동일하게 버퍼링한다.
  const applyTextFilters = () => {
    let next = filters;
    fields
      .filter((f) => f.type === "text" || f.type === "date")
      .forEach((f) => {
        const value = textValuesRef.current[f.name];
        if (value === undefined) return;
        if (f.type === "date" && f.validate && !f.validate(value)) return;
        next = updateFilters(
          next,
          f.name,
          f.operator ?? defaultOperator(f.type),
          value
        );
      });
    setFilters(next);
  };

  const handleTextKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: SearchFieldConfig
  ) => {
    if (e.key !== "Enter") return;
    // 포커스된 필드 값도 버퍼에 먼저 반영한 뒤 전체 적용
    textValuesRef.current[field.name] = (e.target as HTMLInputElement).value;
    applyTextFilters();
  };

  const handleReset = () => {
    textValuesRef.current = {};
    setResetToken((token) => token + 1);

    if (onReset) {
      onReset();
      return;
    }
    // 기본 동작: fields에 선언된 필터만 제거(외부 필터 보존).
    // useDataGrid의 기본 setFilters 동작이 "replace"(전체 배열 교체)임에 의존한다.
    // "merge" 동작이 필요한 화면은 onReset prop으로 직접 구현할 것.
    const managed = new Set(fields.map((f) => f.name));
    setFilters(
      filters.filter((f) => !("field" in f) || !managed.has(f.field))
    );
  };

  const activeCount = filters.filter(
    (f) => "field" in f && fields.some((x) => x.name === f.field)
  ).length;

  const renderField = (field: SearchFieldConfig) => {
    const current = getFilterValue(filters, field.name);
    const itemSx: SxProps<Theme> | undefined = field.width
      ? {
          minWidth:
            typeof field.width === "number" ? `${field.width}px` : field.width,
        }
      : undefined;

    if (field.type === "select") {
      return (
        <TextField
          // 비제어 select에서 defaultValue 사용 시 발생하는 warning 회피
          key={`${field.name}-${resetToken}-${current || "empty"}`}
          id={`search-field-${field.name}`}
          select
          fullWidth
          size="small"
          label={field.label}
          defaultValue={current}
          helperText={field.helperText}
          sx={itemSx}
          onChange={(e) =>
            applyFilter(field.name, field.operator ?? "eq", e.target.value)
          }
        >
          {(field.options ?? []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.type === "date") {
      return (
        <TextField
          key={`${field.name}-${resetToken}`}
          id={`search-field-${field.name}`}
          type="date"
          fullWidth
          size="small"
          label={field.label}
          defaultValue={current}
          helperText={field.helperText}
          InputLabelProps={{ shrink: true }}
          sx={itemSx}
          // text 필드와 동일하게 버퍼에만 담아두고, Enter/검색 버튼으로 확정 적용한다.
          // (날짜를 정하는 즉시 검색되던 것 + 확정 안 한 값이 새로고침 후에도 남던 문제 수정)
          onChange={(e) => {
            textValuesRef.current[field.name] = e.target.value;
          }}
          onKeyDown={(e) =>
            handleTextKeyDown(
              e as React.KeyboardEvent<HTMLInputElement>,
              field
            )
          }
        />
      );
    }

    return (
      <TextField
        key={`${field.name}-${resetToken}`}
        id={`search-field-${field.name}`}
        type="search"
        fullWidth
        size="small"
        label={field.label}
        placeholder={field.placeholder}
        defaultValue={current}
        helperText={field.helperText}
        sx={itemSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        onChange={(e) => {
          textValuesRef.current[field.name] = e.target.value;
        }}
        onKeyDown={(e) =>
          handleTextKeyDown(
            e as React.KeyboardEvent<HTMLInputElement>,
            field
          )
        }
      />
    );
  };

  return (
    <Paper
      variant="outlined"
      className={className}
      // mb는 검색 카드와 아래 DataGrid 사이 기본 간격. caller sx가 덮을 수 있게 마지막에 병합.
      sx={{ p: 2, borderRadius: 2, mb: 3, ...sx }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle2">{title}</Typography>
        {activeCount > 0 && (
          <Chip
            size="small"
            label={`필터 ${activeCount}`}
            color="primary"
            variant="outlined"
          />
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 1.5,
        }}
      >
        {slots?.leading}
        {fields.map(renderField)}
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={applyTextFilters}>
          검색
        </Button>
        <Button variant="text" color="inherit" onClick={handleReset}>
          초기화
        </Button>
        {slots?.actions}
      </Stack>
    </Paper>
  );
};
